/**
 * Kana — hiragana + katakana
 *
 * hiragana กับ katakana เสียงตรงกัน 1:1 → เก็บตารางเดียวแล้ว generate 2 ชุด
 * แก้ตารางที่เดียว ทั้งสองระบบตามทันที
 *
 * alts = การสะกดที่ยอมรับด้วย
 *   Hepburn (shi, tsu) เป็นค่าหลัก — เป็นมาตรฐานป้าย/พาสปอร์ต (NOTES.md §6)
 *   Nihon-shiki (si, tu) รับด้วยเพราะเจอบน logo/ปกหนังสือ
 */
import type { Card } from '@/core/types'
import type { Deck, Group } from './types'

/** [hiragana, katakana, romaji, alts, แถว] */
type Row = readonly [string, string, string, readonly string[], string]

const GOJUON: readonly Row[] = [
  ['あ', 'ア', 'a', [], 'a'],
  ['い', 'イ', 'i', [], 'a'],
  ['う', 'ウ', 'u', [], 'a'],
  ['え', 'エ', 'e', [], 'a'],
  ['お', 'オ', 'o', [], 'a'],

  ['か', 'カ', 'ka', [], 'ka'],
  ['き', 'キ', 'ki', [], 'ka'],
  ['く', 'ク', 'ku', [], 'ka'],
  ['け', 'ケ', 'ke', [], 'ka'],
  ['こ', 'コ', 'ko', [], 'ka'],

  ['さ', 'サ', 'sa', [], 'sa'],
  ['し', 'シ', 'shi', ['si'], 'sa'],
  ['す', 'ス', 'su', [], 'sa'],
  ['せ', 'セ', 'se', [], 'sa'],
  ['そ', 'ソ', 'so', [], 'sa'],

  ['た', 'タ', 'ta', [], 'ta'],
  ['ち', 'チ', 'chi', ['ti'], 'ta'],
  ['つ', 'ツ', 'tsu', ['tu'], 'ta'],
  ['て', 'テ', 'te', [], 'ta'],
  ['と', 'ト', 'to', [], 'ta'],

  ['な', 'ナ', 'na', [], 'na'],
  ['に', 'ニ', 'ni', [], 'na'],
  ['ぬ', 'ヌ', 'nu', [], 'na'],
  ['ね', 'ネ', 'ne', [], 'na'],
  ['の', 'ノ', 'no', [], 'na'],

  ['は', 'ハ', 'ha', [], 'ha'],
  ['ひ', 'ヒ', 'hi', [], 'ha'],
  ['ふ', 'フ', 'fu', ['hu'], 'ha'],
  ['へ', 'ヘ', 'he', [], 'ha'],
  ['ほ', 'ホ', 'ho', [], 'ha'],

  ['ま', 'マ', 'ma', [], 'ma'],
  ['み', 'ミ', 'mi', [], 'ma'],
  ['む', 'ム', 'mu', [], 'ma'],
  ['め', 'メ', 'me', [], 'ma'],
  ['も', 'モ', 'mo', [], 'ma'],

  ['や', 'ヤ', 'ya', [], 'ya'],
  ['ゆ', 'ユ', 'yu', [], 'ya'],
  ['よ', 'ヨ', 'yo', [], 'ya'],

  ['ら', 'ラ', 'ra', [], 'ra'],
  ['り', 'リ', 'ri', [], 'ra'],
  ['る', 'ル', 'ru', [], 'ra'],
  ['れ', 'レ', 're', [], 'ra'],
  ['ろ', 'ロ', 'ro', [], 'ra'],

  ['わ', 'ワ', 'wa', [], 'wa'],
  // を เป็น particle เท่านั้น ออกเสียง o — รับทั้ง wo (เขียน) และ o (พูด) · NOTES.md §6
  ['を', 'ヲ', 'wo', ['o'], 'wa'],

  // ん เป็นคอลัมน์ของตัวเองในตาราง 五十音図 — ไม่ได้อยู่แถว わ
  ['ん', 'ン', 'n', ['nn'], 'n'],
]

const DAKUTEN: readonly Row[] = [
  ['が', 'ガ', 'ga', [], 'ga'],
  ['ぎ', 'ギ', 'gi', [], 'ga'],
  ['ぐ', 'グ', 'gu', [], 'ga'],
  ['げ', 'ゲ', 'ge', [], 'ga'],
  ['ご', 'ゴ', 'go', [], 'ga'],

  ['ざ', 'ザ', 'za', [], 'za'],
  ['じ', 'ジ', 'ji', ['zi'], 'za'],
  ['ず', 'ズ', 'zu', [], 'za'],
  ['ぜ', 'ゼ', 'ze', [], 'za'],
  ['ぞ', 'ゾ', 'zo', [], 'za'],

  ['だ', 'ダ', 'da', [], 'da'],
  // ぢ/づ ปัจจุบันเสียงเดียวกับ じ/ず — เจอน้อยมาก แต่มีใน 続く, 鼻血
  ['ぢ', 'ヂ', 'ji', ['di', 'dzi'], 'da'],
  ['づ', 'ヅ', 'zu', ['du', 'dzu'], 'da'],
  ['で', 'デ', 'de', [], 'da'],
  ['ど', 'ド', 'do', [], 'da'],

  ['ば', 'バ', 'ba', [], 'ba'],
  ['び', 'ビ', 'bi', [], 'ba'],
  ['ぶ', 'ブ', 'bu', [], 'ba'],
  ['べ', 'ベ', 'be', [], 'ba'],
  ['ぼ', 'ボ', 'bo', [], 'ba'],

  ['ぱ', 'パ', 'pa', [], 'pa'],
  ['ぴ', 'ピ', 'pi', [], 'pa'],
  ['ぷ', 'プ', 'pu', [], 'pa'],
  ['ぺ', 'ペ', 'pe', [], 'pa'],
  ['ぽ', 'ポ', 'po', [], 'pa'],
]

const YOON: readonly Row[] = [
  ['きゃ', 'キャ', 'kya', [], 'kya'],
  ['きゅ', 'キュ', 'kyu', [], 'kya'],
  ['きょ', 'キョ', 'kyo', [], 'kya'],

  ['しゃ', 'シャ', 'sha', ['sya'], 'sha'],
  ['しゅ', 'シュ', 'shu', ['syu'], 'sha'],
  ['しょ', 'ショ', 'sho', ['syo'], 'sha'],

  ['ちゃ', 'チャ', 'cha', ['tya', 'cya'], 'cha'],
  ['ちゅ', 'チュ', 'chu', ['tyu', 'cyu'], 'cha'],
  ['ちょ', 'チョ', 'cho', ['tyo', 'cyo'], 'cha'],

  ['にゃ', 'ニャ', 'nya', [], 'nya'],
  ['にゅ', 'ニュ', 'nyu', [], 'nya'],
  ['にょ', 'ニョ', 'nyo', [], 'nya'],

  ['ひゃ', 'ヒャ', 'hya', [], 'hya'],
  ['ひゅ', 'ヒュ', 'hyu', [], 'hya'],
  ['ひょ', 'ヒョ', 'hyo', [], 'hya'],

  ['みゃ', 'ミャ', 'mya', [], 'mya'],
  ['みゅ', 'ミュ', 'myu', [], 'mya'],
  ['みょ', 'ミョ', 'myo', [], 'mya'],

  ['りゃ', 'リャ', 'rya', [], 'rya'],
  ['りゅ', 'リュ', 'ryu', [], 'rya'],
  ['りょ', 'リョ', 'ryo', [], 'rya'],

  ['ぎゃ', 'ギャ', 'gya', [], 'gya'],
  ['ぎゅ', 'ギュ', 'gyu', [], 'gya'],
  ['ぎょ', 'ギョ', 'gyo', [], 'gya'],

  ['じゃ', 'ジャ', 'ja', ['jya', 'zya'], 'ja'],
  ['じゅ', 'ジュ', 'ju', ['jyu', 'zyu'], 'ja'],
  ['じょ', 'ジョ', 'jo', ['jyo', 'zyo'], 'ja'],

  ['びゃ', 'ビャ', 'bya', [], 'bya'],
  ['びゅ', 'ビュ', 'byu', [], 'bya'],
  ['びょ', 'ビョ', 'byo', [], 'bya'],

  ['ぴゃ', 'ピャ', 'pya', [], 'pya'],
  ['ぴゅ', 'ピュ', 'pyu', [], 'pya'],
  ['ぴょ', 'ピョ', 'pyo', [], 'pya'],

  // ぢゃ ชุดนี้แทบไม่เจอในภาษาปัจจุบัน — ใส่ไว้ให้ครบ แท็ก rare ปิดได้
  ['ぢゃ', 'ヂャ', 'ja', ['dya'], 'dja'],
  ['ぢゅ', 'ヂュ', 'ju', ['dyu'], 'dja'],
  ['ぢょ', 'ヂョ', 'jo', ['dyo'], 'dja'],
]

const RARE_ROWS = new Set(['dja'])
/** ぢ づ ตัวเดี่ยว — เจอน้อยเหมือนกัน */
const RARE_CHARS = new Set(['ぢ', 'づ'])

/**
 * ตำแหน่งในคอลัมน์ของตาราง 五十音図 — เรื่องการแสดงผลล้วน ไม่ใช่ความจริงทางภาษา
 *
 * ตารางพื้นฐาน/dakuten เรียงตามสระ a i u e o (5 ช่อง)
 * ตารางควบเรียงตาม ゃ ゅ ょ (3 ช่อง) → きゃ きゅ きょ ต้องอยู่ติดกัน
 * ん ไม่มีสระ วางท้ายคอลัมน์ให้ตรงกับ を เหมือน DJT
 */
function slotOf(romaji: string, group: string): number {
  const v = romaji.at(-1) ?? ''
  if (group === 'yoon') return { a: 0, u: 1, o: 2 }[v] ?? 0
  return { a: 0, i: 1, u: 2, e: 3, o: 4 }[v] ?? 4
}

function build(rows: readonly Row[], set: 'hiragana' | 'katakana', group: string): Card[] {
  const isHira = set === 'hiragana'
  return rows.map(([hira, kata, romaji, alts, row]) => {
    const glyph = isHira ? hira : kata
    const tags = [set, group, `${set}:${row}`, `row:${row}`]
    if (RARE_ROWS.has(row) || RARE_CHARS.has(hira)) tags.push('rare')

    return {
      id: `kana:${glyph}`,
      deck: 'kana' as const,
      tags,
      front: glyph,
      back: romaji,
      ...(alts.length > 0 ? { alts: [...alts] } : {}),
      extra: {
        /** คู่ของอีกระบบ — โชว์ตอนเฉลย "ตัวนี้เขียน katakana ว่า..." */
        pair: isHira ? kata : hira,
        set,
        row,
        /** แถวที่เท่าไรในคอลัมน์ของตาราง 五十音図 */
        slot: slotOf(romaji, group),
        /** ชื่อไฟล์เสียง — เสียงเดียวกันทั้งสองระบบ ใช้ hiragana เป็น key */
        audio: hira,
      },
    } satisfies Card
  })
}

const cards: Card[] = [
  ...build(GOJUON, 'hiragana', 'basic'),
  ...build(DAKUTEN, 'hiragana', 'dakuten'),
  ...build(YOON, 'hiragana', 'yoon'),
  ...build(GOJUON, 'katakana', 'basic'),
  ...build(DAKUTEN, 'katakana', 'dakuten'),
  ...build(YOON, 'katakana', 'yoon'),
]

const ROW_LABEL: Record<string, string> = {
  a: 'あ', ka: 'か', sa: 'さ', ta: 'た', na: 'な',
  ha: 'は', ma: 'ま', ya: 'や', ra: 'ら', wa: 'わ', n: 'ん',
  ga: 'が', za: 'ざ', da: 'だ', ba: 'ば', pa: 'ぱ',
  kya: 'きゃ', sha: 'しゃ', cha: 'ちゃ', nya: 'にゃ', hya: 'ひゃ',
  mya: 'みゃ', rya: 'りゃ', gya: 'ぎゃ', ja: 'じゃ', bya: 'びゃ',
  pya: 'ぴゃ', dja: 'ぢゃ',
}

const groups: Group[] = [
  { tag: 'hiragana', label: 'ひらがな', section: 'ระบบ' },
  { tag: 'katakana', label: 'カタカナ', section: 'ระบบ' },
  { tag: 'basic', label: 'พื้นฐาน 46', section: 'ชุด' },
  { tag: 'dakuten', label: '゛゜ 25', section: 'ชุด' },
  { tag: 'yoon', label: 'ควบ 36', section: 'ชุด' },
  ...Object.entries(ROW_LABEL).map(([row, label]) => ({
    tag: `row:${row}`,
    label,
    section: 'แถว',
  })),
]

export const kanaDeck: Deck = {
  id: 'kana',
  titleJa: 'かなドリル',
  titleTh: 'ฝึกจำ hiragana + katakana',
  modes: ['auto', 'lesson', 'glyph-to-romaji', 'audio-to-glyph', 'romaji-to-glyph'],
  scheduling: 'drill',
  cards,
  groups,
  gridSection: 'แถว',
  gridSplit: 'ระบบ',
  gridSubsets: [
    { tags: ['basic', 'dakuten'], label: '', slots: 5 },
    { tags: ['yoon'], label: 'ควบ', slots: 3 },
  ],
}

/** ใช้ตอน generate ไฟล์เสียง — เสียงไม่ซ้ำ ไม่ต้องอัด 2 รอบ */
export const UNIQUE_SOUNDS: ReadonlyArray<{ kana: string; romaji: string }> = [
  ...GOJUON,
  ...DAKUTEN,
  ...YOON,
].map(([hira, , romaji]) => ({ kana: hira, romaji }))
